import React, { useState, useRef, useEffect } from 'react';
import './VolumeControl.css';

const VolumeControl = ({ volume = 1, onVolumeChange, className = '' }) => {
  const [localVolume, setLocalVolume] = useState(volume);
  const [isDragging, setIsDragging] = useState(false);
  const [isSliderVisible, setIsSliderVisible] = useState(false);
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null); // 用于延迟隐藏
  const previousVolumeRef = useRef(0.5); // 保存静音前的音量，默认 0.5

  // 同步外部 volume
  useEffect(() => {
    setLocalVolume(volume);
    if (volume > 0) {
      previousVolumeRef.current = volume;
    }
  }, [volume]);

  // 处理音量变化
  const handleVolumeChange = (newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setLocalVolume(clampedVolume);
    if (onVolumeChange) {
      onVolumeChange(clampedVolume);
    }
  };

  // 鼠标拖拽相关
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    updateVolumeFromEvent(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updateVolumeFromEvent(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateVolumeFromEvent = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    let y = e.clientY - rect.top;
    y = Math.max(0, Math.min(y, rect.height));
    const newVolume = 1 - (y / rect.height);
    handleVolumeChange(newVolume);
  };

  // 点击图标：切换静音
  const handleIconClick = () => {
    if (localVolume === 0) {
      // 当前是静音，取消静音，恢复之前音量
      const restoredVolume = previousVolumeRef.current || 0.5;
      handleVolumeChange(restoredVolume);
    } else {
      // 当前有音量，静音并保存当前值
      previousVolumeRef.current = localVolume;
      handleVolumeChange(0);
    }
  };

  // 鼠标进入：显示滑块
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsSliderVisible(true);
  };

  // 鼠标离开：延迟 3 秒隐藏
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      // 检查是否鼠标仍在容器或滑块内
      if (!isHovered()) {
        setIsSliderVisible(false);
      }
    }, 3000);
  };

  // 检查鼠标是否仍在组件区域内
  const isHovered = () => {
    if (!containerRef.current) return false;
    const rect = containerRef.current.getBoundingClientRect();
    const { clientX, clientY } = { clientX: 0, clientY: 0 }; // 不能直接获取，需用事件
    // 我们通过 document 的 mouseover 事件判断，但这里我们使用 ref 来辅助判断
    return false; // 我们用 setTimeout + 点击外部逻辑来处理
  };

  // 点击外部关闭（包括自动隐藏后）
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSliderVisible && containerRef.current && !containerRef.current.contains(event.target)) {
        setIsSliderVisible(false);
      }
    };

    if (isSliderVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSliderVisible]);

  // 拖拽事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // 清理 timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 计算滑块填充和显示音量
  const fillHeight = `${localVolume * 100}%`;
  const displayVolume = Math.round(localVolume * 100);

  // 判断是否静音
  const isMuted = localVolume === 0;

  return (
    <div
      ref={containerRef}
      className={`volume-control ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 主音量图标 */}
      <div
        className="volume-icon"
        onClick={handleIconClick}
        title={isMuted ? '取消静音' : '静音'}
      >
        {!isMuted && (
          <svg t="1756283428934" class="icon" viewBox="0 0 1024 1024" width="24" height="24" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5475"><path d="M529.1 901.6c-2.7 0-12.1-4.1-17-9l-0.3-0.3-230.4-190.1H96.1c-8.9 0-18.3-4.6-22.7-9-4.4-4.4-9-13.8-9-22.7v-319c0-7 2.7-12.9 4.4-14.6H71l2.4-2.4c4.4-4.4 13.8-9 22.7-9h185.3l230.4-195.8 0.2-0.2c5.8-5.8 12.9-7 17.8-7 4.4 0 8.9 1 12.6 2.9l0.5 0.3 0.5 0.2c12.5 4.2 17.2 11.3 17.2 26.4v717.8c0 15.1-4.7 22.3-17.2 26.4l-1.8 0.6-1.4 1.4c-3 3-5.6 3.1-11.1 3.1z m279.1-79.7c-9.9 0-23.1-5.1-26.9-12.6-9-18-5.8-36.4 7.9-46.1 4.8-2 11.6-7.2 20.3-15.6 8.9-8.7 22.4-23.7 36.2-45.7 23-36.8 50.4-99.7 50.4-190.9s-29-154.2-53.3-191.1c-14.5-22-28.8-37-38.2-45.6-6.6-6.1-14.9-13-21-15.5-12.6-9.1-17-31.4-8.9-44.5 9.4-9.1 20.6-14.3 30.8-14.3 5.4 0 10.4 1.4 14.8 4.2 0.7 0.6 1.6 1.3 3 2.4 29.4 23.1 54.9 51.4 75.8 84.1 40.1 62.9 60.5 137 60.5 220.3 0 83.7-19.7 158.1-58.4 221.1-20.1 32.7-44.5 60.9-72.6 83.6-1.7 1.4-2.6 2.1-3.3 2.8-3.4 3.4-13.3 3.4-17.1 3.4zM688.6 696.6c-8.3 0-22.6-9.7-26.9-18.3l-0.2-0.5-0.3-0.4c-8.2-12.4 0.8-30.4 14.5-39.7 6.4-3.4 60.9-35.5 60.9-132.3 0-46.5-18-78.4-33.2-97-16.5-20.2-33.1-29.4-33.7-29.8l-0.6-0.3-0.7-0.2c-5.8-1.9-11.4-8.5-14.3-16.8-2.9-8.3-2.3-16.6 1.4-22.2l0.6-0.9 0.3-1c2.9-8.6 15.6-16.1 27.3-16.1 4.5 0 8.6 1.1 11.7 3.2l2.1 1.4h1.5c4.5 1.7 29.1 14 53.5 41.9 21.7 24.9 47.6 68.1 47.6 132.2 0 72.9-24.5 120.2-45 147.1-22.6 29.5-45.6 42.2-50.4 44.1h-2.4l-2.4 2.4c-3.2 3-5.7 3.2-11.3 3.2z" fill="#ffffff" p-id="5476"></path></svg>
        )}
        {isMuted && (
          <svg t="1756283565815" class="icon" viewBox="0 0 1170 1024" width="24" height="24" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="3192"><path d="M607.351688 1017.975688c-3.510857 0-15.719065-5.319481-22.075844-11.689558l-0.398961-0.385662-299.220779-246.89039H45.022753c-11.563221 0-23.771429-5.971117-29.483221-11.682909C9.814442 741.608727 3.843325 729.407169 3.843325 717.837299V303.562805c0-9.096312 3.504208-16.756364 5.718441-18.963948h2.859221l3.111896-3.118545c5.718442-5.718442 17.92-11.689558 29.476572-11.689559h240.653298l299.220779-254.284467 0.259325-0.259325C592.676571 7.713247 601.899221 6.157299 608.256 6.157299c5.718442 0 11.563221 1.296623 16.364052 3.763532l0.651636 0.392312 0.651637 0.265974c16.231065 5.452468 22.341818 14.668468 22.341818 34.277402v932.212364c0 19.608935-6.110753 28.957922-22.341818 34.284052l-2.340572 0.777974-1.815272 1.821922c-3.896519 3.88987-7.27439 4.022857-14.415793 4.022857z m362.462754-103.503792c-12.853195 0-29.995221-6.629403-34.929039-16.364052-11.689558-23.379117-7.533714-47.276883 10.253298-59.870753 6.237091-2.593247 15.067429-9.355636 26.364676-20.260572 11.563221-11.303896 29.090909-30.786494 47.010909-59.352103 29.875532-47.788883 65.456208-129.482805 65.456208-247.921039 0-118.444883-37.661922-200.258494-69.219741-248.180364-18.830961-28.57226-37.402597-48.054857-49.604156-59.225766-8.577662-7.919377-19.34961-16.882701-27.275636-20.127585-16.364052-11.815896-22.075844-40.780468-11.563221-57.789506 12.208208-11.822545 26.756987-18.571636 40.002494-18.571637 7.015065 0 13.504831 1.815273 19.216623 5.452468 0.910961 0.777974 2.081247 1.688935 3.903169 3.118545 38.180571 30.00187 71.294338 66.752831 98.436987 109.222234 52.077714 81.687273 78.575377 177.916675 78.575377 286.101611 0 108.703584-25.586701 205.325299-75.842494 287.138909-26.105351 42.469403-57.796156 79.094026-94.287792 108.577246-2.207584 1.815273-3.37787 2.726234-4.288831 3.637195-4.415169 4.415169-17.268364 4.415169-22.208831 4.415169z m-155.322182-162.729558c-10.778597 0-29.350234-12.600519-34.935689-23.76478l-0.259324-0.651636-0.385663-0.518649c-10.65226-16.104727 1.037299-39.483844 18.830961-51.559065 8.311688-4.415169 79.087377-46.106597 79.087377-171.819221 0-60.389403-23.379117-101.814857-43.11439-125.971948-21.430857-26.231688-42.988052-38.180571-43.766026-38.699221l-0.784623-0.398961-0.904312-0.252675c-7.533714-2.466909-14.808104-11.037922-18.571636-21.81652-3.770182-10.778597-2.992208-21.563844 1.815273-28.831584l0.777974-1.170286 0.392312-1.296623c3.763532-11.170909 20.260571-20.912208 35.454337-20.912208 5.844779 0 11.170909 1.42961 15.193766 4.155844l2.726234 1.815273h1.94826c5.851429 2.214234 37.794909 18.185974 69.485714 54.418286 28.179948 32.335792 61.812364 88.436364 61.812364 171.686233 0 94.680104-31.817143 156.106805-58.441143 191.035845-29.350234 38.320208-59.219117 54.810597-65.449558 57.277506h-3.125195l-3.111896 3.118546c-4.155844 3.896519-7.400727 4.155844-14.675117 4.155844z" fill="#ffffff" p-id="3193"></path><path d="M172.732773 30.10898m28.210806 28.210806l846.324168 846.324168q28.210806 28.210806 0 56.421611l0 0q-28.210806 28.210806-56.421611 0l-846.324169-846.324168q-28.210806-28.210806 0-56.421611l0 0q28.210806-28.210806 56.421612 0Z" fill="#ffffff" p-id="3194"></path><path d="M1056.66826 895.241974a53.194805 53.194805 0 1 1-75.224104 75.224104L135.114805 124.143377A53.194805 53.194805 0 1 1 210.345558 48.912623l846.329351 846.329351z m-9.402182 9.402182L200.936727 58.314805a39.896104 39.896104 0 0 0-56.413091 56.42639l846.322702 846.322701a39.896104 39.896104 0 1 0 56.41974-56.41974z" fill="#ffffff" p-id="3195"></path></svg>
        )}
      </div>

      {/* 音量滑块容器 */}
      <div className={`volume-slider-container ${isSliderVisible ? 'show' : ''}`}>
        {/* 顶部音量数值 */}
        <div className="volume-display">{displayVolume}%</div>

        {/* 音量滑块 */}
        <div
          ref={sliderRef}
          className="volume-slider"
          onMouseDown={handleMouseDown}
        >
          <div className="volume-track">
            <div className="volume-fill" style={{ height: fillHeight }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolumeControl;